import type {
  AcceptInviteResponse,
  CreateInviteRequest,
  CreateInviteResponse,
  CreateOrgRequest,
  InvitePreview,
  ListOrgsResponse,
  OrgInvite,
  OrgMember,
  OrgRole,
  OrgSummary,
  Organization,
  PlatformUserRow,
  UpdateMemberRequest,
  UpdateOrgRequest,
} from '@jam/shared';
import { authHeaders } from './auth-headers';
import { logger } from './logger';

const API_BASE = '/api';

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    logger.warn('orgs-client.get failed', {
      path,
      status: res.status,
      body: body.slice(0, 200),
    });
    throw new Error(`${res.status} ${body}`);
  }
  return (await res.json()) as T;
}

async function sendJson<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('orgs-client.send failed', {
      method,
      path,
      status: res.status,
      body: text.slice(0, 200),
    });
    throw new Error(`${res.status} ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * `GET /api/orgs`. For a regular member, returns the orgs they belong to.
 * For a superAdmin, returns every org on the platform. `primaryOrgId` is
 * the user's preferred active org (may be null if never set).
 */
export async function listMemberships(): Promise<ListOrgsResponse> {
  return getJson<ListOrgsResponse>('/orgs');
}

export async function getOrg(orgId: string): Promise<Organization> {
  return getJson<Organization>(`/orgs/${encodeURIComponent(orgId)}`);
}

export async function updateOrg(
  orgId: string,
  patch: UpdateOrgRequest,
): Promise<Organization> {
  return sendJson<Organization>(
    'PATCH',
    `/orgs/${encodeURIComponent(orgId)}`,
    patch,
  );
}

export async function createOrg(
  req: CreateOrgRequest,
): Promise<Organization> {
  return sendJson<Organization>('POST', '/orgs', req);
}

/**
 * SuperAdmin-only: list every org. Uses the same `/orgs` endpoint as
 * {@link listMemberships}; the server includes every org in the response
 * when the caller is a superAdmin.
 */
export async function listAllOrgs(): Promise<OrgSummary[]> {
  const resp = await getJson<ListOrgsResponse>('/orgs');
  return resp.organizations;
}

export async function listOrgMembers(orgId: string): Promise<OrgMember[]> {
  const { members } = await getJson<{ members: OrgMember[] }>(
    `/orgs/${encodeURIComponent(orgId)}/members`,
  );
  return members;
}

export async function updateOrgMember(
  orgId: string,
  uid: string,
  patch: UpdateMemberRequest,
): Promise<OrgMember> {
  return sendJson<OrgMember>(
    'PATCH',
    `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`,
    patch,
  );
}

export async function removeOrgMember(
  orgId: string,
  uid: string,
): Promise<void> {
  await sendJson<void>(
    'DELETE',
    `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`,
  );
}

export async function listOrgInvites(orgId: string): Promise<OrgInvite[]> {
  const { invites } = await getJson<{ invites: OrgInvite[] }>(
    `/orgs/${encodeURIComponent(orgId)}/invites`,
  );
  return invites;
}

export async function createOrgInvite(
  orgId: string,
  req: CreateInviteRequest,
): Promise<CreateInviteResponse> {
  return sendJson<CreateInviteResponse>(
    'POST',
    `/orgs/${encodeURIComponent(orgId)}/invites`,
    req,
  );
}

export async function revokeOrgInvite(
  orgId: string,
  token: string,
): Promise<void> {
  await sendJson<void>(
    'DELETE',
    `/orgs/${encodeURIComponent(orgId)}/invites/${encodeURIComponent(token)}`,
  );
}

/** Unauthenticated-friendly preview — no `X-Org-Id` needed. */
export async function getInvitePreview(token: string): Promise<InvitePreview> {
  const res = await fetch(
    `${API_BASE}/invites/${encodeURIComponent(token)}`,
    {
      headers: await authHeaders(),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${text}`);
  }
  return (await res.json()) as InvitePreview;
}

export async function acceptInvite(
  token: string,
): Promise<AcceptInviteResponse> {
  return sendJson<AcceptInviteResponse>(
    'POST',
    `/invites/${encodeURIComponent(token)}/accept`,
  );
}

// --- Platform superadmin endpoints ----------------------------------------

export async function listPlatformUsers(): Promise<PlatformUserRow[]> {
  const { users } = await getJson<{ users: PlatformUserRow[] }>(
    '/platform/users',
  );
  return users;
}

export async function grantSuperAdmin(uid: string): Promise<void> {
  await sendJson<void>(
    'POST',
    `/platform/users/${encodeURIComponent(uid)}/grant-superadmin`,
  );
}

export async function revokeSuperAdmin(uid: string): Promise<void> {
  await sendJson<void>(
    'POST',
    `/platform/users/${encodeURIComponent(uid)}/revoke-superadmin`,
  );
}

export type { OrgRole };
